export function createReferralService({ fetcher = fetch, root = '/', shop, page, tracker, attributeNames = ['__ref', '__visit_id', '__sub_id'] }) {
  async function json(url, init, failure = 'service') {
    const response = await fetcher(url, { ...init, signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error(failure);
    return response.json();
  }
  const getCart = () => json(`${root}cart.js`, { cache: 'no-store' });
  const update = body => json(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }, 'save');
  const codes = cart => [...new Set((cart.discount_codes || []).map(item => item.code).filter(Boolean))];
  async function lookup(code) {
    for (const key of ['coupon', 'ref_code']) {
      const url = new URL('https://api-server-3.goaffpro.com/v1/sdk/affiliate');
      url.search = new URLSearchParams({ [key]: code, fields: 'id,ref_code,name', 'x-shop': shop });
      const affiliate = await json(url.href, { credentials: 'omit' });
      if (!affiliate.error && affiliate.id && affiliate.ref_code) return affiliate;
    }
    throw new Error('invalid');
  }
  return {
    getCart,
    readSaved() {
      const ref = tracker.getCookie('ref');
      return ref && tracker.getCookie('fw_kol_ref') === ref ? { ref, code: tracker.getCookie('fw_kol_code'), name: tracker.getCookie('fw_kol_name'), coupon: tracker.getCookie('fw_kol_coupon') } : null;
    },
    async save(value) {
      const code = value.trim();
      if (!code || code.length > 80 || /[\s,<>]/.test(code)) throw new Error('invalid');
      const affiliate = await lookup(code);
      const previous = await getCart();
      const visitUrl = new URL('https://api2.goaffpro.com/shop');
      visitUrl.search = new URLSearchParams({ ref: affiliate.ref_code, shop, location: page });
      const visit = await json(visitUrl.href, { credentials: 'omit' });
      if (visit.error || !visit.visit_id) throw new Error('service');
      const previousCodes = codes(previous);
      const oldCoupon = tracker.getCookie('fw_kol_coupon');
      const retainedCodes = previousCodes.filter(item => !oldCoupon || item.toUpperCase() !== oldCoupon.toUpperCase());
      const coupon = visit.discount_code || '';
      const nextCodes = [...new Set([...retainedCodes, ...(coupon ? [coupon] : [])])];
      const [refName, visitName, sourceName] = attributeNames;
      const attributes = {
        [refName]: affiliate.ref_code,
        [visitName]: String(visit.visit_id),
        [sourceName]: '',
        __formwynn_kol_code: code,
        __formwynn_kol_coupon: coupon,
        __formwynn_kol_name: affiliate.name || ''
      };
      let cart = await update({ attributes, ...(nextCodes.length <= 5 ? { discount: nextCodes.join(',') } : {}) });
      if (cart.attributes?.[refName] !== affiliate.ref_code || String(cart.attributes?.[visitName]) !== String(visit.visit_id)) throw new Error('save');
      if (cart.total_price > previous.total_price) cart = await update({ discount: previousCodes.join(',') });
      const verified = await getCart();
      tracker.setCookie('ref', affiliate.ref_code, visit.cookie_duration);
      tracker.setCookie('gfp_v_id', String(visit.visit_id), visit.cookie_duration);
      tracker.deleteCookie('source');
      if (coupon) tracker.setCookie('dcode', coupon, visit.cookie_duration);
      for (const [key, value] of Object.entries({ fw_kol_ref: affiliate.ref_code, fw_kol_code: code, fw_kol_name: affiliate.name || code, fw_kol_coupon: coupon })) tracker.setCookie(key, value, visit.cookie_duration);
      return { ref: affiliate.ref_code, name: affiliate.name || code, code, cart: verified };
    },
    async remove() {
      const cart = await getCart();
      const coupon = tracker.getCookie('fw_kol_coupon');
      const remaining = codes(cart).filter(code => !coupon || code.toUpperCase() !== coupon.toUpperCase());
      const [refName, visitName, sourceName] = attributeNames;
      await update({ discount: remaining.join(','), attributes: { [refName]: '', [visitName]: '', [sourceName]: '', __formwynn_kol_code: '', __formwynn_kol_coupon: '', __formwynn_kol_name: '' } });
      for (const key of ['ref', 'gfp_v_id', 'source', 'dcode', 'discount_code', 'gfp_ref_expires', 'fw_kol_ref', 'fw_kol_code', 'fw_kol_name', 'fw_kol_coupon']) tracker.deleteCookie(key);
      return getCart();
    }
  };
}

if (typeof window !== 'undefined' && !customElements.get('formwynn-kol-referral')) {
  let busy = false;
  const root = window.Shopify?.routes?.root || '/';
  async function service() {
    for (let attempt = 0; attempt < 80; attempt++) {
      if (window.gfp_cookieManager && window.__goaffpro?.shop) {
        if (!window.__fwKolSdkGate) throw new Error('service');
        await window.__fwKolSdkGate.wait();
        return createReferralService({ fetcher: window.__fwKolSdkGate.nativeFetch, root, shop: window.__goaffpro.shop, page: `${location.origin}${location.pathname}`, tracker: window.gfp_cookieManager, attributeNames: window.__goaffpro.cart_attributes_names });
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    throw new Error('service');
  }
  function notify(detail) {
    document.querySelectorAll('formwynn-kol-referral').forEach(element => element.show(detail));
  }
  function updateReferralUrl(ref) {
    const url = new URL(location.href);
    let changed = false;
    for (const key of window.__goaffpro?.identifiers || ['ref']) {
      if (url.searchParams.has(key)) { url.searchParams.delete(key); changed = true; }
    }
    if (changed) {
      if (ref) url.searchParams.set('ref', ref);
      history.replaceState(history.state, '', url.href);
    }
  }
  async function refreshSections() {
    const drawerScroll = document.querySelector('[data-cart-drawer-scroll-region]')?.scrollTop || 0;
    const fullCart = document.querySelector('#main-cart-footer');
    const fullItems = document.querySelector('#main-cart-items');
    const ids = ['cart-drawer', 'cart-icon-bubble'];
    if (fullCart?.dataset.id) ids.push(fullCart.dataset.id);
    if (fullItems?.dataset.id) ids.push(fullItems.dataset.id);
    const url = new URL(location.href);
    url.searchParams.set('sections', ids.join(','));
    const response = await fetch(url.href, { cache: 'no-store' });
    if (!response.ok) return;
    const sections = await response.json();
    const drawer = document.querySelector('cart-drawer');
    const targets = [['cart-drawer', '#CartDrawer'], ['cart-icon-bubble', '#cart-icon-bubble']];
    if (fullCart?.dataset.id) targets.push([fullCart.dataset.id, '#main-cart-footer']);
    if (fullItems?.dataset.id) targets.push([fullItems.dataset.id, '#main-cart-items']);
    for (const [key, selector] of targets) {
      if (!sections[key]) continue;
      const node = new DOMParser().parseFromString(sections[key], 'text/html').querySelector(selector);
      const current = document.querySelector(selector);
      if (node && current) current.innerHTML = node.innerHTML;
    }
    drawer?.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
    const scrollRegion = drawer?.querySelector('[data-cart-drawer-scroll-region]');
    if (scrollRegion) scrollRegion.scrollTop = drawerScroll;
    if (drawer?.classList.contains('active')) drawer.querySelector('formwynn-kol-referral input')?.focus({ preventScroll: true });
  }
  class KolReferral extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.input = this.querySelector('input');
      this.status = this.querySelector('[data-kol-status]');
      this.querySelector('[data-kol-apply]').addEventListener('click', () => this.apply());
      this.querySelector('[data-kol-remove]').addEventListener('click', () => this.apply(true));
      this.input.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); this.apply(); } });
      service().then(api => {
        const saved = api.readSaved();
        if (saved && !busy && this.isConnected) this.show({ ...saved, saved: true });
      }).catch(() => {});
    }
    show(detail) {
      this.setAttribute('aria-busy', String(Boolean(detail.busy)));
      this.querySelectorAll('input, button').forEach(control => { control.disabled = Boolean(detail.busy); });
      if (detail.code !== undefined) this.input.value = detail.code;
      this.querySelector('[data-kol-remove]').hidden = !detail.saved;
      const key = detail.busy ? 'checking' : detail.error ? `error${detail.error}` : detail.saved ? 'saved' : 'removed';
      this.status.textContent = (this.dataset[key] || this.dataset.errorservice || '').replace('[name]', detail.name || '');
      this.status.dataset.error = String(Boolean(detail.error));
      this.input.setAttribute('aria-invalid', String(detail.error === 'invalid'));
    }
    async apply(remove = false) {
      if (busy) return;
      busy = true;
      const code = this.input.value;
      notify({ busy: true, code });
      const checkoutButtons = [...document.querySelectorAll('button[name="checkout"]')].map(button => ({ button, disabled: button.disabled }));
      checkoutButtons.forEach(({ button }) => { button.disabled = true; });
      const accelerated = [...document.querySelectorAll('.cart__dynamic-checkout-buttons, .additional-checkout-buttons')].map(element => ({ element, inert: element.inert }));
      accelerated.forEach(({ element }) => { element.inert = true; });
      try {
        const api = await service();
        const result = remove ? await api.remove() : await api.save(code);
        updateReferralUrl(remove ? null : result.ref);
        window.dispatchEvent(new CustomEvent('goaffproReferralCode', { detail: { ref: remove ? null : result.ref } }));
        await refreshSections().catch(() => {});
        notify(remove ? { code: '', saved: false } : { ...result, saved: true });
      } catch (error) {
        const reason = ['invalid', 'save'].includes(error.message) ? error.message : 'service';
        notify({ error: reason, code });
      } finally {
        busy = false;
        checkoutButtons.forEach(({ button, disabled }) => { if (button.isConnected) button.disabled = disabled; });
        accelerated.forEach(({ element, inert }) => { if (element.isConnected) element.inert = inert; });
      }
    }
  }
  customElements.define('formwynn-kol-referral', KolReferral);
  document.addEventListener('click', event => {
    if (busy && event.target.closest('[name="checkout"],a[href*="/checkout"]')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
}
